<script lang="ts" setup>
import { segmentation, segmentationV1 } from '@thecompaniesapi/shared/client'

const { getCompanyAttributes } = useApiAttributes()
const { refreshResponseWithApiVersion, version } = useApiDoc()
const { t } = useI18n()
const api = useApi()
const localePath = useLocalePath()

const { data: response, execute, status } = useAsyncData(
  'doc:companies-enrich-from-social-network-url',
  () => {
    const linkedin = version.value === 'v1'
      ? segmentationV1.freeQueries.linkedin
      : segmentation.freeQueries.linkedin

    return api.companies.fetchBySocial({ linkedin })
  },
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/companies/by-social?linkedin=https://www.linkedin.com/company/microsoft',
    statuses: [
      {
        code: 200,
        color: 'emerald',
        description: t('statuses.200'),
      },
      {
        code: 404,
        color: 'amber',
        description: t('statuses.404'),
      },
    ],
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.dribbble'),
    example: '/companies/by-social?dribbble=https://dribbble.com/microsoft',
    name: 'dribbble',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.facebook'),
    example: '/companies/by-social?facebook=https://www.facebook.com/microsoft',
    name: 'facebook',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.github'),
    example: '/companies/by-social?github=https://github.com/microsoft',
    name: 'github',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.instagram'),
    example: '/companies/by-social?instagram=https://www.instagram.com/microsoft',
    name: 'instagram',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.linkedin'),
    example: '/companies/by-social?linkedin=https://www.linkedin.com/company/microsoft',
    name: 'linkedin',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.pinterest'),
    example: '/companies/by-social?pinterest=https://www.pinterest.com/microsoft',
    name: 'pinterest',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.snapchat'),
    example: '/companies/by-social?snapchat=https://www.snapchat.com/add/microsoft',
    name: 'snapchat',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.tiktok'),
    example: '/companies/by-social?tiktok=https://www.tiktok.com/@microsoft',
    name: 'tiktok',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.twitter'),
    example: '/companies/by-social?twitter=https://twitter.com/microsoft',
    name: 'twitter',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.wellfound'),
    example: '/companies/by-social?wellfound=https://wellfound.com/company/microsoft',
    name: 'wellfound',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.youtube'),
    example: '/companies/by-social?youtube=https://www.youtube.com/@microsoft',
    name: 'youtube',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.refresh'),
    example: '/companies/by-social?linkedin=https://www.linkedin.com/company/microsoft&refresh=true',
    name: 'refresh',
    optional: true,
    type: 'boolean',
  },
]

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getCompanyAttributes({ version: version.value }),
      description: t('response.company'),
      link: {
        label: t('globals.browseAllDatapoints'),
        to: localePath({
          name: 'api-slug',
          params: { slug: 'datapoints' },
        }),
      },
      name: 'company',
      type: 'object',
    },
  ]
})

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :level-to-open="1"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "dribbble": "The company's Dribbble URL.",
      "facebook": "The company's Facebook URL.",
      "github": "The company's GitHub URL.",
      "instagram": "The company's Instagram URL.",
      "linkedin": "The company's LinkedIn URL. We handle both alpha urls (/company/microsoft) and numeric ids (/company/6585).",
      "pinterest": "The company's Pinterest URL.",
      "refresh": "When set to true, triggers a real-time refresh of the company data including website crawling, social media analysis, and AI-powered enrichment. Note: This operation costs 10 additional credits per request.",
      "snapchat": "The company's Snapchat URL.",
      "tiktok": "The company's TikTok URL.",
      "twitter": "The company's Twitter URL.",
      "wellfound": "The company's Wellfound URL.",
      "youtube": "The company's YouTube URL."
    },
    "response": {
      "company": "The company profile from our database."
    },
    "statuses": {
      "200": "OK",
      "404": "Not found"
    }
  },
  "es": {
    "attributes": {
      "dribbble": "La URL Dribbble de la empresa.",
      "facebook": "La URL Facebook de la empresa.",
      "github": "La URL GitHub de la empresa.",
      "instagram": "La URL Instagram de la empresa.",
      "linkedin": "La URL LinkedIn de la empresa. Gestionamos tanto las URLs alfa (/company/microsoft) como los identificadores numéricos (/company/6585).",
      "pinterest": "La URL Pinterest de la empresa.",
      "refresh": "Cuando se establece en true, activa una actualización en tiempo real de los datos de la empresa, incluyendo rastreo del sitio web, análisis de redes sociales y enriquecimiento mediante IA. Nota: esta operación cuesta 10 créditos adicionales por solicitud.",
      "snapchat": "La URL Snapchat de la empresa.",
      "tiktok": "La URL TikTok de la empresa.",
      "twitter": "La URL Twitter de la empresa.",
      "wellfound": "La URL Wellfound de la empresa.",
      "youtube": "La URL YouTube de la empresa."
    },
    "response": {
      "company": "El perfil de empresa de nuestra base de datos."
    },
    "statuses": {
      "200": "OK",
      "404": "No encontrado"
    }
  },
  "fr": {
    "attributes": {
      "dribbble": "L'URL Dribbble de l'entreprise.",
      "facebook": "L'URL Facebook de l'entreprise.",
      "github": "L'URL GitHub de l'entreprise.",
      "instagram": "L'URL Instagram de l'entreprise.",
      "linkedin": "L'URL LinkedIn de l'entreprise. Nous gérons à la fois les URL alpha (/company/microsoft) et les identifiants numériques (/company/6585).",
      "pinterest": "L'URL Pinterest de l'entreprise.",
      "refresh": "Lorsqu'il est activé (true), déclenche une mise à jour en temps réel des données de l'entreprise, incluant l'exploration du site web, l'analyse des réseaux sociaux et l'enrichissement via l'IA. Remarque : cette opération coûte 10 crédits supplémentaires par requête.",
      "snapchat": "L'URL Snapchat de l'entreprise.",
      "tiktok": "L'URL TikTok de l'entreprise.",
      "twitter": "L'URL Twitter de l'entreprise.",
      "wellfound": "L'URL Wellfound de l'entreprise.",
      "youtube": "L'URL YouTube de l'entreprise."
    },
    "response": {
      "company": "Le profil d'entreprise de notre base de données."
    },
    "statuses": {
      "200": "OK",
      "404": "Non trouvée"
    }
  }
}
</i18n>
